/**
 * FeedbackCapture component.
 *
 * Renders an in-chat customer satisfaction feedback card with a two-step flow:
 * 1. Binary feedback (thumbs up / thumbs down) + skip
 * 2. Star rating (1-5 stars) + skip (shown only after binary selection)
 *
 * After submission or skip at any stage, a confirmation message appears and
 * the component collapses. The component is non-intrusive — it does not block
 * continued conversation.
 *
 * The backend triggers this by sending a message with type "feedback_request".
 * Feedback data is emitted as a structured event for the communication layer
 * to send to a dedicated API endpoint (not as a chat message).
 */

import type {
  ResolvedWidgetConfig,
  FeedbackRating,
  FeedbackStage,
  FeedbackEvent,
  FeedbackCallbacks,
  FeedbackTriggerPoint,
} from "../types";
import {
  THUMBS_UP_SVG,
  THUMBS_DOWN_SVG,
  STAR_FILLED_SVG,
  STAR_OUTLINE_SVG,
} from "./icons";

export interface FeedbackCaptureOptions {
  config: ResolvedWidgetConfig;
  callbacks: FeedbackCallbacks;
  /** Conversation ID this feedback relates to */
  conversationId: string;
  /** Custom prompt text (overrides default) */
  prompt?: string;
  /**
   * REQ-C-030: trigger point identifier for analytics segmentation.
   * The widget round-trips this back to the backend in the FeedbackEvent
   * so the persisted row's `channel` column carries the trigger
   * provenance.
   */
  triggerPoint?: FeedbackTriggerPoint;
}

/** Default prompt shown when no custom prompt is provided */
const DEFAULT_FEEDBACK_PROMPT = "How was your experience?";

/** Total number of stars in the rating scale */
const STAR_COUNT = 5;

export class FeedbackCapture {
  private readonly element: HTMLElement;
  private stage: FeedbackStage = "binary";
  private rating: FeedbackRating | null = null;
  private stars: number | null = null;
  private hoveredStar: number | null = null;

  private readonly promptEl: HTMLElement;
  private readonly contentEl: HTMLElement;

  constructor(private readonly options: FeedbackCaptureOptions) {
    const prompt = options.prompt ?? DEFAULT_FEEDBACK_PROMPT;

    this.element = document.createElement("div");
    this.element.className = "hana-feedback";
    this.element.setAttribute("role", "region");
    this.element.setAttribute("aria-label", "Feedback request");

    // Prompt text
    this.promptEl = document.createElement("p");
    this.promptEl.className = "hana-feedback__prompt";
    this.promptEl.textContent = prompt;
    this.element.appendChild(this.promptEl);

    // Content container (holds whichever stage is active)
    this.contentEl = document.createElement("div");
    this.contentEl.className = "hana-feedback__content";
    this.element.appendChild(this.contentEl);

    this.renderBinaryStage();
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getStage(): FeedbackStage {
    return this.stage;
  }

  getRating(): FeedbackRating | null {
    return this.rating;
  }

  getStars(): number | null {
    return this.stars;
  }

  // ---- Stage rendering ----

  private renderBinaryStage(): void {
    this.contentEl.innerHTML = "";

    const binaryRow = document.createElement("div");
    binaryRow.className = "hana-feedback__binary";

    // Thumbs up
    const upBtn = this.createIconButton(
      THUMBS_UP_SVG,
      "hana-feedback__thumb hana-feedback__thumb--up",
      "Rate positive",
    );
    upBtn.addEventListener("click", () => this.handleBinary("positive"));

    // Thumbs down
    const downBtn = this.createIconButton(
      THUMBS_DOWN_SVG,
      "hana-feedback__thumb hana-feedback__thumb--down",
      "Rate negative",
    );
    downBtn.addEventListener("click", () => this.handleBinary("negative"));

    binaryRow.appendChild(upBtn);
    binaryRow.appendChild(downBtn);
    this.contentEl.appendChild(binaryRow);

    // Skip button
    this.contentEl.appendChild(this.createSkipButton());
  }

  private renderStarsStage(): void {
    this.contentEl.innerHTML = "";
    this.stage = "stars";

    const starsRow = document.createElement("div");
    starsRow.className = "hana-feedback__stars";
    starsRow.setAttribute("role", "radiogroup");
    starsRow.setAttribute("aria-label", "Star rating from 1 to 5");

    for (let i = 1; i <= STAR_COUNT; i++) {
      const starBtn = document.createElement("button");
      starBtn.className = "hana-feedback__star";
      starBtn.setAttribute("type", "button");
      starBtn.setAttribute("role", "radio");
      starBtn.setAttribute("aria-checked", "false");
      starBtn.setAttribute("aria-label", `${i} star${i > 1 ? "s" : ""}`);
      starBtn.setAttribute("data-star", String(i));
      starBtn.tabIndex = i === 1 ? 0 : -1;

      starBtn.innerHTML = `<span class="hana-feedback__star-icon">${STAR_OUTLINE_SVG}</span>`;

      starBtn.addEventListener("click", () => this.handleStarClick(i));
      starBtn.addEventListener("mouseenter", () =>
        this.handleStarHover(i, starsRow),
      );
      starBtn.addEventListener("mouseleave", () =>
        this.handleStarHover(null, starsRow),
      );
      starBtn.addEventListener("keydown", (e) =>
        this.handleStarKeydown(e, i, starsRow),
      );

      starsRow.appendChild(starBtn);
    }

    this.contentEl.appendChild(starsRow);

    // Skip button
    this.contentEl.appendChild(this.createSkipButton());
  }

  private renderComplete(): void {
    this.contentEl.innerHTML = "";
    this.stage = "complete";

    const confirmEl = document.createElement("div");
    confirmEl.className = "hana-feedback__confirmation";
    confirmEl.setAttribute("role", "status");
    confirmEl.textContent = "Thank you for your feedback!";
    this.contentEl.appendChild(confirmEl);

    // Update prompt to reflect completion
    this.promptEl.textContent = "";
  }

  private renderDismissed(): void {
    this.contentEl.innerHTML = "";
    this.stage = "dismissed";

    const dismissEl = document.createElement("div");
    dismissEl.className = "hana-feedback__dismissed";
    dismissEl.setAttribute("role", "status");
    dismissEl.textContent = "Feedback skipped.";
    this.contentEl.appendChild(dismissEl);

    this.promptEl.textContent = "";
  }

  // ---- Event handlers ----

  private handleBinary(selection: FeedbackRating): void {
    if (this.stage !== "binary") return;
    this.rating = selection;
    this.renderStarsStage();
  }

  private handleStarClick(star: number): void {
    if (this.stage !== "stars") return;
    this.stars = star;
    this.emitFeedback();
    this.renderComplete();
  }

  private handleStarHover(star: number | null, container: HTMLElement): void {
    this.hoveredStar = star;
    this.updateStarVisuals(container);
  }

  private handleStarKeydown(
    e: KeyboardEvent,
    currentStar: number,
    container: HTMLElement,
  ): void {
    let targetStar: number | null = null;

    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      targetStar = currentStar < STAR_COUNT ? currentStar + 1 : 1;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      targetStar = currentStar > 1 ? currentStar - 1 : STAR_COUNT;
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.handleStarClick(currentStar);
      return;
    }

    if (targetStar !== null) {
      const buttons = container.querySelectorAll(
        ".hana-feedback__star",
      ) as NodeListOf<HTMLButtonElement>;
      buttons.forEach((btn, idx) => {
        btn.tabIndex = idx + 1 === targetStar ? 0 : -1;
      });
      const targetBtn = container.querySelector(
        `[data-star="${targetStar}"]`,
      ) as HTMLButtonElement | null;
      targetBtn?.focus();
    }
  }

  private handleSkip(): void {
    if (this.stage === "binary") {
      this.emitFeedback();
      this.renderDismissed();
    } else if (this.stage === "stars") {
      // Binary was selected but stars skipped
      this.emitFeedback();
      this.renderComplete();
    }
  }

  // ---- Helpers ----

  private createIconButton(
    svgContent: string,
    className: string,
    ariaLabel: string,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = className;
    btn.setAttribute("type", "button");
    btn.setAttribute("aria-label", ariaLabel);
    btn.innerHTML = `<span class="hana-feedback__thumb-icon">${svgContent}</span>`;
    return btn;
  }

  private createSkipButton(): HTMLButtonElement {
    const skipBtn = document.createElement("button");
    skipBtn.className = "hana-feedback__skip";
    skipBtn.setAttribute("type", "button");
    skipBtn.setAttribute("aria-label", "Skip feedback");
    skipBtn.textContent = "Skip";
    skipBtn.addEventListener("click", () => this.handleSkip());
    return skipBtn;
  }

  private updateStarVisuals(container: HTMLElement): void {
    const activeStar = this.hoveredStar ?? this.stars ?? 0;
    const buttons = container.querySelectorAll(
      ".hana-feedback__star",
    ) as NodeListOf<HTMLButtonElement>;

    buttons.forEach((btn) => {
      const starValue = parseInt(btn.getAttribute("data-star") ?? "0", 10);
      const iconEl = btn.querySelector(".hana-feedback__star-icon");
      if (iconEl) {
        iconEl.innerHTML =
          starValue <= activeStar ? STAR_FILLED_SVG : STAR_OUTLINE_SVG;
      }
      btn.setAttribute(
        "aria-checked",
        starValue === this.stars ? "true" : "false",
      );
    });
  }

  private emitFeedback(): void {
    const event: FeedbackEvent = {
      rating: this.rating,
      stars: this.stars,
      conversationId: this.options.conversationId,
      entityId: this.options.config.entityId,
      timestamp: new Date().toISOString(),
    };
    if (this.options.triggerPoint) {
      // REQ-C-030: forward trigger point to the backend so the
      // FeedbackEntry row records which natural endpoint produced
      // the prompt (post_booking / post_resolution / pre_exit).
      event.triggerPoint = this.options.triggerPoint;
    }
    this.options.callbacks.onSubmit(event);
  }
}
